/**
 * The onboarding template definition, as the editor understands it.
 *
 * The definition is a single JSON document owned by the backend
 * (MyLedger.Application.Onboarding.OnboardingTemplateDefinition). The editor gives forms to the
 * parts an operator actually tailors per client — packs, roles, tenders, control accounts — and
 * leaves the rest as JSON.
 *
 * Everything here therefore works by PATCHING a parsed object rather than rebuilding one. A form
 * that reconstructs the document from its own fields silently deletes whatever it does not know
 * about, and a template that quietly loses its category strategy provisions a company whose
 * revenue does not split. Unknown keys survive an edit untouched.
 */

export interface RoleBinding {
  role: string;
  accountCode: string;
  /** COMPANY (default) or VOUCHER_GROUP: one voucher category's own account. */
  scopeType?: string | null;
  /** The voucher category, e.g. EXCHANGE, when scopeType is VOUCHER_GROUP. */
  scopeCode?: string | null;
}

export interface TenderMode {
  code: string;
  name: string;
  modeType?: string;
  clearingAccountCode?: string;
  mdrRate?: number;
  mdrExpenseAccountCode?: string;
  perCostCenterCash?: boolean;
}

export interface AccountSpec {
  code: string;
  name: string;
  type: string;
  nature?: string;
  parentCode?: string | null;
  isPosting?: boolean;
}

export interface PartySpec { controlAccountCode?: string; defaultCreditDays?: number }

export interface TemplateDefinition {
  schemaVersion?: number;
  industry?: string;
  baseSetup?: {
    coaPack?: string;
    currencies?: string[];
    financialYears?: { mode?: string; fiscalStartMonth?: number; yearsBack?: number; yearsForward?: number; periodType?: string };
    defaultLocation?: { code?: string; name?: string };
    defaultCostCenter?: { code?: string; name?: string };
    createIntegrationApiKey?: boolean;
    createAdminUser?: boolean;
    [k: string]: unknown;
  };
  accounts?: AccountSpec[];
  companyRoles?: RoleBinding[];
  tenders?: { modes?: TenderMode[]; [k: string]: unknown };
  parties?: { supplier?: PartySpec; customer?: PartySpec; [k: string]: unknown };
  postingRulePacks?: string[];
  activateRules?: boolean;
  /** categoryStrategy, accountGenerators, dimensions and anything added later. */
  [k: string]: unknown;
}

/** Sections the editor shows as forms. Everything else is preserved and shown as JSON. */
export const FORM_SECTIONS = [
  'schemaVersion', 'industry', 'baseSetup', 'accounts', 'companyRoles',
  'tenders', 'parties', 'postingRulePacks', 'activateRules',
] as const;

export const parseDefinition = (json: string): { def: TemplateDefinition | null; error: string | null } => {
  try {
    const parsed = JSON.parse(json);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed))
      return { def: null, error: 'The definition must be a JSON object.' };
    return { def: parsed as TemplateDefinition, error: null };
  } catch (e) {
    return { def: null, error: e instanceof Error ? e.message : 'Invalid JSON.' };
  }
};

/** The keys the forms do not cover — surfaced as JSON so nothing is invisible. */
export const advancedKeys = (def: TemplateDefinition): string[] =>
  Object.keys(def).filter(k => !FORM_SECTIONS.includes(k as typeof FORM_SECTIONS[number]));

export const advancedJson = (def: TemplateDefinition): string => {
  const out: Record<string, unknown> = {};
  for (const k of advancedKeys(def)) out[k] = def[k];
  return JSON.stringify(out, null, 2);
};

/**
 * Replaces the advanced sections while keeping every form-owned section exactly as it is. Keys
 * dropped from the JSON are dropped from the definition — removing a section has to be possible —
 * but a form section can never be reached this way.
 */
export const withAdvanced = (
  def: TemplateDefinition,
  advanced: Record<string, unknown>,
): TemplateDefinition => {
  const out: Record<string, unknown> = {};
  for (const k of FORM_SECTIONS) if (k in def) out[k] = (def as Record<string, unknown>)[k];
  for (const [k, v] of Object.entries(advanced))
    if (!FORM_SECTIONS.includes(k as typeof FORM_SECTIONS[number])) out[k] = v;
  return out as TemplateDefinition;
};

/** A one-line description of what a template installs, for the list screen. */
export const summarise = (json: string): string => {
  const { def } = parseDefinition(json);
  if (!def) return 'Definition could not be read';
  const bits: string[] = [];
  if (def.baseSetup?.coaPack) bits.push(def.baseSetup.coaPack);
  if (def.postingRulePacks?.length) bits.push(`${def.postingRulePacks.length} rule packs`);
  if (def.companyRoles?.length) bits.push(`${def.companyRoles.length} roles`);
  if (def.tenders?.modes?.length) bits.push(`${def.tenders.modes.length} tenders`);
  if (def.accounts?.length) bits.push(`${def.accounts.length} extra accounts`);
  return bits.join(' · ') || 'Empty definition';
};
